// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/channel/components/MessageRoleOffers.module.css';
import Channels from '@app/features/channel/state/Channels';
import {RoleName} from '@app/features/guild/components/RoleName';
import Guilds from '@app/features/guild/state/Guilds';
import {roleNameToPlaintext} from '@app/features/guild/utils/RoleNameText';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as RoleOfferCommands from '@app/features/messaging/commands/RoleOfferCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import * as ColorUtils from '@app/features/theme/utils/ColorUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const ADD_ROLE_DESCRIPTOR = msg({
	message: 'Add role {roleName}',
	comment: 'Accessible label for claiming a self-assignable role badge on a message. Preserve {roleName}.',
});
const REMOVE_ROLE_DESCRIPTOR = msg({
	message: 'Remove role {roleName}',
	comment: 'Accessible label for removing a self-assignable role badge on a message. Preserve {roleName}.',
});

interface MessageRoleOffersProps {
	message: Message;
	isPreview?: boolean;
	disableInteraction?: boolean;
}

function resolveMessageGuildId(message: Message): string | null {
	return Channels.getChannel(message.channelId)?.guildId ?? message.guildId ?? null;
}

export const MessageRoleOffers = observer(function MessageRoleOffers({
	message,
	isPreview = false,
	disableInteraction = false,
}: MessageRoleOffersProps) {
	const {i18n} = useLingui();
	const [pending, setPending] = useState<{roleId: string; wantHasRole: boolean} | null>(null);
	const guildId = resolveMessageGuildId(message);
	const guild = guildId ? Guilds.getGuild(guildId) : null;
	const userId = Authentication.currentUserId;
	const member = guildId && userId ? GuildMembers.getMember(guildId, userId) : null;

	const pendingSatisfied =
		pending != null && member != null && member.roles.has(pending.roleId) === pending.wantHasRole;

	useEffect(() => {
		if (pendingSatisfied) {
			setPending(null);
		}
	}, [pendingSatisfied]);

	const handleToggle = useCallback(
		async (roleId: string) => {
			if (isPreview || disableInteraction || pending) {
				return;
			}
			const hasRole = member?.roles.has(roleId) ?? false;
			setPending({roleId, wantHasRole: !hasRole});
			try {
				await RoleOfferCommands.toggleRoleOffer(message, roleId);
			} catch {
				setPending(null);
			}
		},
		[disableInteraction, isPreview, member, message, pending],
	);

	if (!guild || message.roleOffers.length === 0) {
		return null;
	}

	const offers = message.roleOffers
		.map((roleId) => guild.roles[roleId])
		.filter((role): role is NonNullable<typeof role> => role != null && !role.isEveryone);

	if (offers.length === 0) {
		return null;
	}

	const interactionLocked = disableInteraction || isPreview || pending != null;

	return (
		<div className={styles.offersGrid} data-flx="channel.message-role-offers.offers-grid">
			{offers.map((role) => {
				const selected = member?.roles.has(role.id) ?? false;
				const roleName = roleNameToPlaintext(role.name);
				const label = i18n._(selected ? REMOVE_ROLE_DESCRIPTOR : ADD_ROLE_DESCRIPTOR, {roleName});
				return (
					<FocusRing key={role.id} offset={-2} data-flx="channel.message-role-offers.focus-ring">
						<button
							type="button"
							className={clsx(styles.offerButton, selected && styles.offerSelected)}
							aria-label={label}
							aria-pressed={selected}
							disabled={interactionLocked}
							onClick={() => {
								void handleToggle(role.id);
							}}
							data-flx="channel.message-role-offers.offer-button"
						>
							<span
								className={styles.roleIndicator}
								style={{backgroundColor: ColorUtils.int2rgb(role.color)}}
								data-flx="channel.message-role-offers.role-indicator"
							/>
							<span className={styles.roleName} data-flx="channel.message-role-offers.role-name">
								<RoleName name={role.name} data-flx="channel.message-role-offers.role-name" />
							</span>
						</button>
					</FocusRing>
				);
			})}
		</div>
	);
});
